import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid from '@mui/material/Grid';
import type React from 'react';
import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import RichText from '@/components/RichText/RichText';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import type { ButtonCf, CTATypeCF } from '@/types';
import { MAX_IMAGE_WIDTH, setImgWidth } from '@/utils/frontify';
import useStyles from './Confirmation.styles';

const isCTA = (content: ButtonCf | CTATypeCF): content is CTATypeCF => {
  return content.sys.contentType.sys.id === 'cta';
};

const getCTAContent = (content: ButtonCf | CTATypeCF | undefined) => {
  if (!content) return;

  if (isCTA(content)) {
    return { url: content.fields.url, label: content.fields.text };
  } else {
    return { url: content.fields.url, label: content.fields.label };
  }
};

interface ConfirmationProps {
  heading: string | undefined;
  description?: RichTextDocument;
  hideImageOnMobile?: boolean;
  imageSrc: string | undefined;
  imageAltText: string | undefined;
  buttonContent?: ButtonCf | CTATypeCF;
  isOnDark: boolean;
  isVisible: boolean;
  reverseLayout?: boolean;
  fixedGrid?: boolean;
}

const Confirmation: React.FC<ConfirmationProps> = ({
  heading,
  description,
  hideImageOnMobile = false,
  imageSrc,
  imageAltText,
  buttonContent,
  isOnDark,
  isVisible,
  reverseLayout,
  fixedGrid,
}) => {
  const { classes, cx } = useStyles({
    isOnDark,
    hideImageOnMobile,
  });
  const { classes: globalClasses } = useGlobalStyles({ fixed: false });
  const cta = getCTAContent(buttonContent);
  const { isMobile } = useViewPortSize();
  const imageSize = isMobile ? 768 : 1440;

  return (
    <GridContainer
      gridProps={{
        className: cx(
          isVisible ? classes.visible : classes.hidden,
          reverseLayout ? classes.reverseLayout : classes.layout
        ),
      }}
      fixed={fixedGrid}
    >
      <Grid
        className={cx(reverseLayout && classes.reverseContent)}
        size={{
          xs: 12,
          sm: 9,
          md: 6,
        }}
      >
        <div
          className={cx(
            fixedGrid ? globalClasses.h2 : globalClasses.h1,
            classes.heading
          )}
        >
          {heading}
        </div>
        {description && (
          <div className={globalClasses.body}>
            <RichText
              content={description}
              isOnDark={isOnDark}
              blockClasses={{ p: classes.richTextParagraph }}
            />
          </div>
        )}
        {cta && (
          <Button
            type='button'
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.MD}
            label={cta.label}
            href={cta.url}
            className={classes.button}
            isOnDark={isOnDark}
          />
        )}
      </Grid>
      <Grid
        className={classes.hideImageOnMobile}
        size={{
          xs: 12,
          md: 6,
        }}
      >
        {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
        <img
          src={setImgWidth(imageSrc, MAX_IMAGE_WIDTH)}
          className={classes.landerImage}
          alt={imageAltText}
          width={imageSize}
        />
      </Grid>
    </GridContainer>
  );
};

export default Confirmation;
